import { pubText, pubPath, pubFormat } from "../i18n/publicTranslation";
import React, { useState } from 'react';
import { Box, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { featuredMusicals } from '../data/featuredMusicals';
import FadeVideo, { SOFT_MEDIA_EDGE_FADE } from './common/FadeVideo';
import { createMusicalUrl } from '../util/urlHelpers';

interface MusicalCarouselProps {
  onMusicalChange?: (musicalIndex: number) => void;
}

const MusicalCarousel: React.FC<MusicalCarouselProps> = ({ onMusicalChange }) => {
  const [currentIndex, setCurrentIndex] = useState(1); // Start at middle item
  const navigate = useNavigate();

  const handlePrev = () => {
    const newIndex = (currentIndex - 1 + featuredMusicals.length) % featuredMusicals.length;
    setCurrentIndex(newIndex);
    if (onMusicalChange) {
      onMusicalChange(newIndex);
    }
  };

  const handleNext = () => {
    const newIndex = (currentIndex + 1) % featuredMusicals.length;
    setCurrentIndex(newIndex);
    if (onMusicalChange) {
      onMusicalChange(newIndex);
    }
  };

  const handleDotClick = (index: number) => {
    setCurrentIndex(index);
    if (onMusicalChange) {
      onMusicalChange(index);
    }
  };

  const handleMusicalClick = (title: string, storyId: string) => {
    navigate(pubPath(createMusicalUrl(title, storyId)));
  };

  const getItemPosition = (index: number) => {
    const diff = (index - currentIndex + featuredMusicals.length) % featuredMusicals.length;
    if (diff === 0) return 'center';
    if (diff === 1 || diff === -(featuredMusicals.length - 1)) return 'right';
    if (diff === featuredMusicals.length - 1 || diff === -1) return 'left';
    return 'hidden';
  };

  return (
    <Box sx={{ width: '100%', maxWidth: '900px', margin: '0 auto' }}>
      {/* Carousel Container - Fixed height to prevent layout shift */}
      <Box
        sx={{
          position: 'relative',
          height: { xs: '360px', sm: '380px', md: '400px' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {pubText(featuredMusicals.map((musical, index) => {
          const position = getItemPosition(index);
          const isCenter = position === 'center';
          const isLeft = position === 'left';
          const isRight = position === 'right';

          return (
            <Box
              key={musical.storyId}
              onClick={() => isCenter && handleMusicalClick(musical.title, musical.storyId)}
              sx={{
                position: 'absolute',
                width: isCenter
                  ? { xs: '300px', sm: '330px', md: '350px' }
                  : { xs: '240px', sm: '260px', md: '280px' },
                cursor: isCenter ? 'pointer' : 'default',
                transition: 'all 0.4s ease-out',
                transform: isCenter
                  ? 'translateX(0) scale(1.1)'
                  : isLeft
                  ? { xs: 'translateX(-180px)', sm: 'translateX(-210px)', md: 'translateX(-240px)' }
                  : isRight
                  ? { xs: 'translateX(180px)', sm: 'translateX(210px)', md: 'translateX(240px)' }
                  : 'scale(0)',
                opacity: position === 'hidden' ? 0 : isCenter ? 1 : 0.55,
                zIndex: isCenter ? 10 : 5,
                pointerEvents: isCenter ? 'auto' : 'none',
              }}
            >
              <FadeVideo
                videoSrc={musical.videoSrc}
                posterSrc={musical.posterSrc}
                alt={pubFormat("__VALUE_0__ musical video", [musical.title])}
                maxWidth="100%"
                borderRadius="10px"
                horizontalFade={SOFT_MEDIA_EDGE_FADE.horizontal}
                verticalFade={{
                  start: SOFT_MEDIA_EDGE_FADE.vertical.start,
                  end: musical.verticalFadeEnd ?? SOFT_MEDIA_EDGE_FADE.vertical.end,
                }}
              />
            </Box>
          );
        }))}

        {/* Navigation Arrows */}
        <IconButton
          onClick={handlePrev}
          sx={{
            position: 'absolute',
            left: { xs: 0, sm: 20, md: 60 },
            zIndex: 20,
            background: 'rgba(0, 0, 0, 0.6)',
            color: '#D8A363',
            width: { xs: 36, md: 44 },
            height: { xs: 36, md: 44 },
            '&:hover': {
              background: 'rgba(216, 163, 99, 0.3)',
              color: '#E8B373',
            },
          }}
        >
          <ChevronLeft sx={{ fontSize: { xs: 24, md: 32 } }} />
        </IconButton>

        <IconButton
          onClick={handleNext}
          sx={{
            position: 'absolute',
            right: { xs: 0, sm: 20, md: 60 },
            zIndex: 20,
            background: 'rgba(0, 0, 0, 0.6)',
            color: '#D8A363',
            width: { xs: 36, md: 44 },
            height: { xs: 36, md: 44 },
            '&:hover': {
              background: 'rgba(216, 163, 99, 0.3)',
              color: '#E8B373',
            },
          }}
        >
          <ChevronRight sx={{ fontSize: { xs: 24, md: 32 } }} />
        </IconButton>
      </Box>
      {/* Dots Indicator */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 1,
          marginTop: '10px',
        }}
      >
        {pubText(featuredMusicals.map((_, index) => (
          <Box
            key={index}
            onClick={() => handleDotClick(index)}
            sx={{
              width: index === currentIndex ? 24 : 8,
              height: 8,
              borderRadius: '4px',
              background: index === currentIndex ? '#D8A363' : 'rgba(255, 255, 255, 0.3)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                background: index === currentIndex ? '#E8B373' : 'rgba(255, 255, 255, 0.5)',
              },
            }}
          />
        )))}
      </Box>
    </Box>
  );
};

export default MusicalCarousel;
